interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  category: string;
  cover_image: string;
  media_files: Array<{ url: string; type: 'image' | 'video'; name: string }> | null;
  file_url: string;
  product_type: string;
  stock_unlimited: boolean;
  stock_quantity: number;
  shipping_info: string;
  weight: number;
}

interface StoreProfile {
  store_name: string;
  store_description: string;
  banner_image: string;
  logo_image: string;
  social_links?: Array<{ id: string; platform: string; label: string; url: string; is_active: boolean }>;
}

interface Profile {
  full_name: string;
  avatar_url: string;
}

interface Props {
  storeProfile: StoreProfile | null;
  userProfile: Profile | null;
  products: Product[];
  storeName: string;
  storeDesc: string;
  onBuyNow: (product: Product) => void;
  onCardClick: (product: Product) => void;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  activeFilter: 'all' | 'digital' | 'physical';
  setActiveFilter: (v: 'all' | 'digital' | 'physical') => void;
  filteredProducts: Product[];
}

function isVideoUrl(url: string): boolean {
  if (!url) return false;
  const lower = url.toLowerCase().split('?')[0];
  return lower.endsWith('.mp4') || lower.endsWith('.mov') || lower.endsWith('.webm') || lower.endsWith('.ogg');
}

const categoryIcons: Record<string, string> = {
  'e-book': 'ri-book-line', 'template': 'ri-file-text-line', 'course': 'ri-graduation-cap-line',
  'audio': 'ri-music-line', 'video': 'ri-video-line', 'software': 'ri-code-box-line',
  'clothing': 'ri-t-shirt-line', 'accessories': 'ri-handbag-line', 'art': 'ri-paint-brush-line',
  'electronics': 'ri-cpu-line', 'books': 'ri-book-open-line', 'other': 'ri-folder-line',
};

const LINK_PLATFORMS: Record<string, { icon: string; color: string }> = {
  website:   { icon: 'ri-global-line',           color: 'text-gray-500' },
  instagram: { icon: 'ri-instagram-line',         color: 'text-pink-500' },
  twitter:   { icon: 'ri-twitter-x-line',         color: 'text-gray-800' },
  facebook:  { icon: 'ri-facebook-circle-line',   color: 'text-blue-600' },
  tiktok:    { icon: 'ri-tiktok-line',            color: 'text-gray-900' },
  youtube:   { icon: 'ri-youtube-line',           color: 'text-red-500' },
  linkedin:  { icon: 'ri-linkedin-box-line',      color: 'text-blue-700' },
  whatsapp:  { icon: 'ri-whatsapp-line',          color: 'text-green-500' },
  telegram:  { icon: 'ri-telegram-line',          color: 'text-sky-500' },
  custom:    { icon: 'ri-links-line',             color: 'text-teal-500' },
};

export default function GridTemplate({ storeProfile, userProfile, products, storeName, storeDesc, onBuyNow, onCardClick, searchQuery, setSearchQuery, activeFilter, setActiveFilter, filteredProducts }: Props) {
  const digitalCount = products.filter(p => p.product_type !== 'physical').length;
  const physicalCount = products.filter(p => p.product_type === 'physical').length;
  const activeLinks = (storeProfile?.social_links || []).filter(l => l.is_active);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Sidebar + Main layout */}
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className="w-64 flex-shrink-0 border-r border-gray-100 sticky top-0 h-screen overflow-y-auto hidden lg:flex flex-col" style={{ background: '#f8f9fa' }}>
          <div className="p-6 border-b border-gray-100">
            {(storeProfile?.logo_image || userProfile?.avatar_url) && (
              <div className="w-14 h-14 rounded-xl overflow-hidden border border-gray-200 mb-3">
                <img src={storeProfile?.logo_image || userProfile?.avatar_url || ''} alt="logo" className="w-full h-full object-cover" />
              </div>
            )}
            <h1 className="text-base font-extrabold text-gray-900 leading-tight">{storeName}</h1>
            <p className="text-xs text-gray-400 mt-1 leading-relaxed line-clamp-3">{storeDesc}</p>
          </div>
          <div className="p-4 space-y-1">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest px-2 mb-3">Filter</p>
            {(['all', 'digital', 'physical'] as const).map(f => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer text-left ${activeFilter === f ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-200'}`}
              >
                <span className="flex items-center gap-2">
                  <i className={f === 'all' ? 'ri-apps-line' : f === 'digital' ? 'ri-download-cloud-line' : 'ri-box-3-line'}></i>
                  {f === 'all' ? 'All Products' : f === 'digital' ? 'Digital' : 'Physical'}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${activeFilter === f ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {f === 'all' ? products.length : f === 'digital' ? digitalCount : physicalCount}
                </span>
              </button>
            ))}
          </div>

          {/* Linktree Section in Sidebar */}
          {activeLinks.length > 0 && (
            <div className="px-4 py-4 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest px-2 mb-3">Links</p>
              <div className="space-y-1.5">
                {activeLinks.map(link => {
                  const meta = LINK_PLATFORMS[link.platform] || LINK_PLATFORMS.custom;
                  return (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50 transition-all group cursor-pointer"
                    >
                      <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                        <i className={`${meta.icon} text-sm`}></i>
                      </div>
                      <span className="text-xs font-semibold text-gray-700 truncate flex-1 group-hover:text-gray-900">{link.label}</span>
                      <i className="ri-external-link-line text-gray-300 text-xs flex-shrink-0"></i>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-auto p-4 border-t border-gray-100">
            <div className="text-xs text-gray-400 space-y-1.5">
              <div className="flex items-center gap-1.5"><i className="ri-shield-check-line text-teal-400"></i> Secure Payments</div>
              <div className="flex items-center gap-1.5"><i className="ri-lock-line text-teal-400"></i> Buyer Protected</div>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0">
          {/* Top bar */}
          <div className="sticky top-0 z-30 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 lg:hidden">
              {(storeProfile?.logo_image || userProfile?.avatar_url) && (
                <div className="w-8 h-8 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                  <img src={storeProfile?.logo_image || userProfile?.avatar_url || ''} alt="logo" className="w-full h-full object-cover" />
                </div>
              )}
              <span className="text-sm font-bold text-gray-900">{storeName}</span>
            </div>
            <div className="relative flex-1 max-w-sm ml-auto">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-400 bg-white"
              />
            </div>
          </div>

          {/* Mobile filter */}
          <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-100 lg:hidden overflow-x-auto">
            {(['all', 'digital', 'physical'] as const).map(f => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${activeFilter === f ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}
              >
                {f === 'all' ? `All (${products.length})` : f === 'digital' ? `Digital (${digitalCount})` : `Physical (${physicalCount})`}
              </button>
            ))}
          </div>

          {/* Mobile Linktree */}
          {activeLinks.length > 0 && (
            <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-100 lg:hidden overflow-x-auto">
              {activeLinks.map(link => {
                const meta = LINK_PLATFORMS[link.platform] || LINK_PLATFORMS.custom;
                return (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-gray-50 hover:border-gray-400 transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
                  >
                    <i className={`${meta.icon} text-xs ${meta.color}`}></i>
                    <span className="text-xs font-semibold text-gray-700">{link.label}</span>
                  </a>
                );
              })}
            </div>
          )}

          {/* Banner */}
          {storeProfile?.banner_image && (
            <div className="w-full h-40 overflow-hidden">
              <img src={storeProfile.banner_image} alt="banner" className="w-full h-full object-cover object-top" />
            </div>
          )}

          <div className="p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-sm font-semibold text-gray-500">
                {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found
              </h2>
            </div>

            {filteredProducts.length === 0 ? (
              <div className="py-24 text-center">
                <i className="ri-store-2-line text-5xl text-gray-200 mb-4 block"></i>
                <p className="text-lg font-bold text-gray-300">{searchQuery ? 'Nothing found' : 'No products yet'}</p>
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="mt-4 px-5 py-2 bg-gray-900 text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap">
                    Clear
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 pb-16">
                {filteredProducts.map(product => {
                  const isPhysical = product.product_type === 'physical';
                  const outOfStock = !product.stock_unlimited && product.stock_quantity <= 0;
                  const firstMedia = product.media_files && product.media_files.length > 0 ? product.media_files[0] : null;
                  const src = firstMedia ? firstMedia.url : product.cover_image;
                  const isVid = firstMedia ? firstMedia.type === 'video' : isVideoUrl(product.cover_image);
                  return (
                    <div
                      key={product.id}
                      onClick={() => onCardClick(product)}
                      className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-gray-300 hover:shadow-md transition-all duration-200 cursor-pointer group flex flex-col"
                    >
                      <div className="relative overflow-hidden bg-gray-50 flex-shrink-0" style={{ minHeight: '140px' }}>
                        {src ? (
                          isVid ? (
                            <video src={src} className="w-full h-auto object-contain" muted autoPlay loop playsInline />
                          ) : (
                            <img src={src} alt={product.title} className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-500" />
                          )
                        ) : (
                          <div className="w-full h-36 flex flex-col items-center justify-center bg-gray-50">
                            <i className={`${categoryIcons[product.category] || 'ri-file-line'} text-4xl text-gray-200 mb-1`}></i>
                          </div>
                        )}
                        <div className="absolute top-2 left-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${isPhysical ? 'bg-orange-500 text-white' : 'bg-teal-500 text-white'}`}>
                            {isPhysical ? 'Physical' : 'Digital'}
                          </span>
                        </div>
                        {outOfStock && (
                          <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                            <span className="text-xs font-bold text-gray-400 border border-gray-200 px-3 py-1 rounded-full">Out of Stock</span>
                          </div>
                        )}
                      </div>
                      <div className="p-3 flex flex-col flex-1">
                        <h3 className="text-xs font-bold text-gray-900 line-clamp-2 mb-1 leading-snug">{product.title}</h3>
                        <div className="flex items-center justify-between mt-auto pt-2">
                          <span className="text-sm font-extrabold text-gray-900">{product.currency} {product.price.toLocaleString()}</span>
                          <button
                            onClick={e => { e.stopPropagation(); if (!outOfStock) onBuyNow(product); }}
                            disabled={outOfStock}
                            className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap ${outOfStock ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : isPhysical ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-teal-500 text-white hover:bg-teal-600'}`}
                          >
                            {outOfStock ? 'N/A' : isPhysical ? 'Buy' : 'Get'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
